import { useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, Mail, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ReminderArtefact, ReminderTone } from './extractArtefacts';

/**
 * Renders a `draftReminder` tool result as an actionable card.
 *
 * The reminder body is a paragraph the AI drafted; the customer-facing
 * send goes through the workspace's `/invoices/:id/remind` pipeline
 * (verified domain, branded template, audit trail). To keep the card
 * focused — and the chat surface scoped to "render the thing, open
 * the real dialog elsewhere" — Send routes the user to the invoice
 * detail page where `RemindInvoiceDialog` lives, with the body
 * pre-staged via URL state.
 *
 * Older entries in the chat tape (the operator asked for a different
 * tone, or a different invoice) collapse to a quieter visual: the
 * Send button hides, the badge goes muted. Mirrors mobile's
 * "superseded" treatment so the user can tell history from active.
 */
export interface ReminderCardProps {
  reminder: ReminderArtefact;
  superseded?: boolean;
}

const TONE_LABEL: Record<ReminderTone, string> = {
  friendly: 'Friendly',
  firm: 'Firm',
  final: 'Final',
};

export function ReminderCard({ reminder, superseded = false }: ReminderCardProps) {
  const [open, setOpen] = useState(false);
  const previewBody = reminder.body.split('\n')[0] ?? '';

  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-card shadow-sm shadow-black/5">
      <div className="h-[3px] bg-accent" />

      <header className="flex items-start gap-3 px-4 pb-2.5 pt-3.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
          <Mail className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            Reminder for {reminder.invoiceNumber} · {reminder.client.name}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <Badge variant="default" className="px-1.5 py-px text-[10px] font-medium">
              {TONE_LABEL[reminder.tone]}
            </Badge>
            <span>
              {reminder.daysOverdue > 0
                ? `${reminder.daysOverdue} day${reminder.daysOverdue === 1 ? '' : 's'} overdue · ${reminder.amountDueFormatted} due`
                : `${reminder.amountDueFormatted} due`}
            </span>
          </p>
        </div>
      </header>

      <div className="px-4 pb-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Subject
        </p>
        <p className="mt-0.5 text-[13px] text-foreground">{reminder.subject}</p>

        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="mt-2 flex w-full items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground"
        >
          Message
          {open ? (
            <ChevronUp className="size-3" />
          ) : (
            <ChevronDown className="size-3" />
          )}
        </button>
        <p
          className={cn(
            'mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-foreground',
            !open && 'line-clamp-2',
          )}
        >
          {open ? reminder.body : previewBody}
        </p>
      </div>

      {!superseded ? (
        <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2.5">
          <p className="text-[11px] text-muted-foreground">
            {reminder.client.email
              ? `Will email ${reminder.client.email}`
              : 'Client has no email on file — add one to send.'}
          </p>
          <Button asChild size="sm" disabled={!reminder.client.email}>
            <Link
              to={`/invoices/${reminder.invoiceId}?remind=1&tone=${reminder.tone}`}
            >
              <Send className="size-3.5" />
              Send reminder
              <ArrowRight className="size-3" />
            </Link>
          </Button>
        </div>
      ) : (
        <div className="border-t border-line bg-secondary/40 px-4 py-2.5">
          <p className="text-[11px] text-muted-foreground">
            Refined below — a newer reminder replaced this one
          </p>
        </div>
      )}
    </article>
  );
}
